import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { tap } from 'rxjs/operators';

// tslint:disable-next-line
import { environment } from './../../../environments/environment';
import { ErrorResponse } from './../../models/error-response';
import { ErrorService } from './../../services/error-service/error.service';
import { ExternalUrlManagerService } from './../../services/external-url-manager/external-url-manager.service';
import { PageHelperService } from './../../services/page-helper/page-helper.service';

import { IonicSafeString } from '@ionic/angular/standalone';
import { Auth0Service } from 'src/app/services/auth0-service/auth0.service';
import { HttpHelperService } from 'src/app/services/http-helper/http-helper.service';
import { FileDisplayErrorModalComponent } from 'src/app/components/modals/file-display-error-modal/file-display-error-modal.component';
import { ModalController } from '@ionic/angular/standalone';

/** Pass untouched request through to the next request handler. */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private errorService = inject(ErrorService);
  private router = inject(Router);
  private pageHelper = inject(PageHelperService);
  private urlManager = inject(ExternalUrlManagerService);
  private auth0Service = inject(Auth0Service);
  private httpHelper = inject(HttpHelperService);
  private modalController = inject(ModalController);

  private apiTimeout: number;
  private countryCallNumber: '18662686879';
  private fileErrorBypassCodes = [
    '/api/profile/auto-id-card/pdf',
    '/api/profile/messages/document/',
    '/api/profile/documents/launch',
    '/api/profile/documents/encrypted/launch',
    '/api/profile/auto-id-card/encrypted/pdf',
  ];
  private noErrorBypassCodes = [
    'cross-sell',
    'documents/details\\?policyNumber=',
    '/api/profile/paperless/update',
    '/api/profile/paperless/not-now',
    '/api/profile/preferences/channels/update',
    '/api/profile/preferences/update',
    'api/profile/billing/dashboard/tiles',
    ...this.fileErrorBypassCodes,
  ];

  constructor() {
    this.apiTimeout = environment.API_TIMEOUT;
  }

  createError(errorResponseObj: any) {
    const errorContent = new ErrorResponse(errorResponseObj);
    return errorContent;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> | any {
    let redirectToLogin = false;
    let noAlert = false;

    // Need to put something in here to logout a user from Auth0 if the error comes from oauth-login screen
    // Means some sort of 500 happened during the login flow
    if (
      (req.url.match('dashboard') && !req.url.match('dashboard/tiles')) ||
      req.url.match('authenticate')
    ) {
      redirectToLogin = true;
    }

    noAlert = this.noErrorBypassCodes.some((s) => req.url.includes(s));

    return next.handle(req).pipe(
      tap(
        () => {},
        (response) => {
          if (response instanceof HttpErrorResponse) {
            // Error Status 0 means we can't connect to the BFF
            // either the BFF is down or you don't have internet connection
            if (response.status === 0) {
              this.showSlowOrNotConnectivityError(redirectToLogin);
              return throwError(response);
            }

            if (response.status === 401 && !response?.error) {
              this.redirect401Error();
              return;
            }

            if (
              response.status === 400 &&
              response.error?.fieldErrors &&
              response.error?.fieldErrors.length > 0
            ) {
              this.present400withFieldErrorsAlert(response.error?.fieldErrors[0]);
              return throwError(response);
            }

            //by going through gateway, when BFF unavailable throws 503 service unavailable
            if (response.status === 503) {
              this.showSlowOrNotConnectivityError(redirectToLogin);
              return throwError(response);
            }

            if (
              this.noErrorBypassCodes.some((s) => req.url.includes(s)) &&
              (response.status === 406 || response.status === 500)
            ) {
              return throwError(response);
            }

            // In the instance that the BFF returns TEXT vs. JSON (currently only for SSO)
            // The error response will also be string.  We need to convert to JSON
            // in order for the error states to handle properly
            let serverErrorResponse = response?.error;
            if (typeof serverErrorResponse != null && typeof serverErrorResponse === 'string') {
              serverErrorResponse = JSON.parse(response.error);
            }
            // Special error codes that need to be handled by the individual pages.
            const bypassCodes = [
              'auth.credentials.invalid',
              'rbr-conversion.verify.person.invalid',
              'submit.claim.auto.glass-lynx-disable',
              'emoney.client-id.not-available.error',
              'policy.auto-image-currently-unavailable',
              'policy.property-image-currently-unavailable',
              'policy.life-image-currently-unavailable',
              'policy.business-image-currently-unavailable',
              'rbr-conversion-user-has-no-bo',
              'auth.account.locked',
            ];
            if (serverErrorResponse?.error) {
              if (bypassCodes.includes(serverErrorResponse.error.code)) {
                // Do nothing, let the page handle this error
                return throwError(response);
              } else {
                const errorResponse = new ErrorResponse(serverErrorResponse.error);
                const errorResponseData = serverErrorResponse.error.data;

                switch (errorResponse.code) {
                  case 'auth.required':
                    this.showGeneralAlert(errorResponse, true);
                    break;
                  case 'auth.rbr-conversion.required':
                    const firstName: string = errorResponseData.firstName || '';
                    const lastName: string = errorResponseData.lastName || '';

                    const errorLinks = errorResponseData.links;
                    let accountConversionUrl = '';
                    let accountConversionAssistedUrl = '';
                    if (errorLinks.length) {
                      // find the object with rel === account-conversion
                      // and return that whole object
                      const accountConversionUrlObject = errorLinks.find((link) => {
                        return link.rel === 'account-conversion';
                      });
                      // use that object and grab the href
                      accountConversionUrl = accountConversionUrlObject.href || '';
                      // find the object with rel === account-conversion-assisted
                      // and return that whole object
                      const accountConversionAssistedUrlObject = errorLinks.find((link) => {
                        return link.rel === 'account-conversion-assisted';
                      });
                      // use that object and grab the href
                      accountConversionAssistedUrl = accountConversionAssistedUrlObject.href || '';
                    }

                    const navigationExtras: NavigationExtras = {
                      replaceUrl: true,
                      state: {
                        firstName,
                        lastName,
                        accountConversionUrl,
                        accountConversionAssistedUrl,
                      },
                    };

                    this.router.navigateByUrl('/account-conversion', navigationExtras);
                    break;
                  case 'rbr-conversion.verify.person-assistance-code-invalid':
                    this.showGeneralAlert(errorResponse);
                    break;
                  case 'insurance-card.info.error':
                    this.showInsuranceIdCardSpecificError(errorResponse);
                    break;
                  case 'auth-user.disable-auth-user-only-profile':
                    this.showDenyAuthUserAlert(errorResponse);
                    break;
                  case 'bill-pay.account.not-eligible':
                    this.pageHelper.closeLoader();
                    const NavigationExtras: NavigationExtras = {
                      state: {
                        addProductDetail: this.httpHelper.getAddProductDetail(),
                        addProductFinancialUrl: this.httpHelper.getAddProductFinanacialSubmit(),
                        addProductBusinessUrl:
                          this.httpHelper.getAddProductCombinedInsuranceSubmit(),
                      },
                    };
                    this.router.navigateByUrl(`app/add-product?error=true`, NavigationExtras);
                    break;
                  case 'recaptcha.verification.failure':
                    this.redirectOnRecaptchaError();
                    break;
                  case 'recaptcha.verification.service.failure':
                    this.redirectOnRecaptchaError();
                    break;
                  default:
                    if (response.status === 401) {
                      // the error will contain the title and message
                      this.redirect401Error();
                    } else if (noAlert) {
                      return throwError(response);
                    } else if (response.status === 404) {
                      this.show404Alert();
                    } else {
                      this.showGeneralAlert(errorResponse, redirectToLogin);
                      return throwError(response);
                    }
                    break;
                }
                return throwError(response);
              }
            } else {
              if (response.status === 401) {
                this.redirect401Error();
                return;
              }
              this.pageHelper.closeLoader();
              this.errorService.addErrors(
                this.createError({
                  message: `Something unexpected happened. Please try again shortly.`,
                }),
              );
              return throwError(response);
            }
          }
          if (response instanceof TimeoutError) {
            this.pageHelper.closeLoader();
            this.showSlowOrNotConnectivityError(redirectToLogin);
          }
          return throwError(response);
        },
      ),
    );
  }

  async showInsuranceIdCardSpecificError(errorResponse: ErrorResponse) {
    const alert = await this.pageHelper.showAlert({
      message: new IonicSafeString(
        `<p>There was a problem retrieving your ID card. Call COUNTRY Financial at <span class="nowrap">1-866-COUNTRY</span> <span class="nowrap">(1-866-268-6879)</span> for assistance with your account.</p>`,
      ),
      title: 'Error',
      cssClass: 'insurance-error',
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Call',
          handler: async () => {
            (await alert).dismiss().then(() => {
              this.urlManager.openUrl(`tel:+${this.countryCallNumber}`, '_system', 'location=yes');
            });
            return false;
          },
        },
      ],
    });
    await alert.present();
  }

  async showInsuranceIdCardPdfSpecificError(autoId: boolean = false) {
    const modal = await this.modalController.create({
      component: FileDisplayErrorModalComponent,
      cssClass: 'claim-modal',
      componentProps: { autoId: autoId, encrypted: true },
    });
    modal.present();
  }

  private showSlowOrNotConnectivityError(redirectToLogin: boolean) {
    this.pageHelper.closeLoader();
    const errorResponse = new ErrorResponse({
      title: 'Connectivity Error',
      message:
        'We couldn’t load this page due to little or no connectivity. Please check your internet connection and try again.',
    });
    this.showGeneralAlert(errorResponse, redirectToLogin);
  }

  async showGeneralAlert(errorResponse: ErrorResponse, redirectLogin: boolean = false) {
    const alert = await this.pageHelper.showAlert({
      header: errorResponse.title,
      message: errorResponse.message,
      backdropDismiss: false,
      buttons: [
        {
          text: 'OK',
          handler: () => {
            if (redirectLogin) {
              this.router.navigateByUrl('login', { replaceUrl: true });
              this.auth0Service.logout();
            }
          },
        },
      ],
    });
    await alert.present();
  }

  async show404Alert() {
    const alert = await this.pageHelper.showAlert({
      message:
        "We're working to resolve an unexpected issue and apologize for the inconvenience. Come back later and try again.",
      backdropDismiss: false,
      buttons: [
        {
          text: 'OK',
          handler: () => {},
        },
      ],
    });
    await alert.present();
  }

  async present400withFieldErrorsAlert(fieldErrors: any) {
    const alert = await this.pageHelper.showAlert({
      header: 'Error',
      message: fieldErrors.message,
      backdropDismiss: false,
      buttons: [
        {
          text: 'OK',
          handler: () => {},
        },
      ],
    });
    await alert.present();
  }

  async redirectOnRecaptchaError() {
    this.router.navigateByUrl('error', { replaceUrl: true });
  }

  private async showDenyAuthUserAlert(errorResponse: ErrorResponse) {
    try {
      window['dl'].hier1 = window['dl'].pageName = 'auth-user-deny';
      window['s'].clearVars();
      window['_satellite'].track('ajax_load');
    } catch (e) {}

    const alert = await this.pageHelper.showAlert({
      header: errorResponse.title,
      message: errorResponse.message,
      backdropDismiss: false,
      buttons: [
        {
          text: 'Go to website',
          handler: async () => {
            this.router.navigateByUrl('login', { replaceUrl: true });
            (await alert).dismiss().then(() => {
              this.urlManager.openUrl('https://my.countryfinancial.com', '_system', 'location=yes');
            });
            return false;
          },
        },
        {
          text: 'Close',
          handler: async () => {
            this.router.navigateByUrl('login', { replaceUrl: true });
            return false;
          },
        },
      ],
    });
    await alert.present();
  }

  private redirect401Error() {
    const redirectUrl = window.location.pathname + window.location.search;
    sessionStorage.setItem('redirectUrl', redirectUrl);
    this.auth0Service.logout();
  }
}
